// Универсальные утилиты для Canvas

// Z-order bases
export const TYPE_Z_BASE = {
    rectangle: 100,
    circle: 100,
    arrow: 200,
    text: 300
};

export const getBaseZ = (el) => {
    if (el.activation) return 260;
    if (el.type === 'arrow' && el.isMessage) return 150;
    if (el.type === 'arrow' && el.lifeline) return 140;
    if (el.type === 'text') return 300;
    return TYPE_Z_BASE[el.type] || 100;
};

export const deepClone = (v) => {
    try {
        // Проверяем, что значение можно сериализовать
        if (v === undefined || v === null) {
            return v;
        }

        // Для примитивных типов возвращаем как есть
        if (typeof v !== 'object') {
            return v;
        }

        // Для массивов
        if (Array.isArray(v)) {
            return v.map(item => deepClone(item));
        }

        // Для объектов создаем новый объект и копируем только сериализуемые свойства
        const cloned = {};
        for (const key in v) {
            if (v.hasOwnProperty(key)) {
                const value = v[key];
                // Пропускаем функции и другие несериализуемые значения
                if (typeof value !== 'function' && typeof value !== 'undefined') {
                    try {
                        cloned[key] = deepClone(value);
                    } catch (error) {
                        // Если не можем клонировать свойство, пропускаем его
                        console.warn(`Не удалось клонировать свойство ${key}:`, error);
                    }
                }
            }
        }
        return cloned;
    } catch (error) {
        console.warn('Ошибка при глубоком клонировании:', error);
        // В качестве fallback пытаемся использовать JSON
        try {
            return JSON.parse(JSON.stringify(v));
        } catch (jsonError) {
            console.warn('Ошибка при JSON клонировании:', jsonError);
            // Если и JSON не работает, возвращаем пустой объект/массив
            return Array.isArray(v) ? [] : {};
        }
    }
};

// Константы
export const VIRTUAL_SIZE = 50000; // Вернем 50000, так как CodeGenerator может располагать элементы далеко
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 5;

// Вычисление bounding box элементов
export const bboxOf = (els) => {
    if (!els || !els.length) return null;
    let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

    els.forEach((el) => {
        if (!el) return;

        // Handle any element with explicit dimensions (rectangle, database, cylinder, diamond, etc.)
        if (el.width !== undefined && el.height !== undefined && el.x !== undefined && el.y !== undefined) {
            minX = Math.min(minX, el.x);
            minY = Math.min(minY, el.y);
            maxX = Math.max(maxX, el.x + el.width);
            maxY = Math.max(maxY, el.y + el.height);
        } else if (el.type === 'circle' || (el.radius !== undefined)) {
            const r = el.radius || 0;
            minX = Math.min(minX, el.x);
            minY = Math.min(minY, el.y);
            maxX = Math.max(maxX, el.x + 2 * r);
            maxY = Math.max(maxY, el.y + 2 * r);
        } else if (el.type === 'arrow') {
            const pts = el.pathPoints?.length
                ? el.pathPoints
                : [
                    { x: el.x, y: el.y },
                    { x: el.x2, y: el.y2 }
                ];
            pts.forEach((p) => {
                minX = Math.min(minX, p.x);
                minY = Math.min(minY, p.y);
                maxX = Math.max(maxX, p.x);
                maxY = Math.max(maxY, p.y);
            });
        } else if (el.type === 'text') {
            minX = Math.min(minX, el.x);
            minY = Math.min(minY, el.y);
            maxX = Math.max(maxX, el.x);
            maxY = Math.max(maxY, el.y);
        }
    });

    if (!isFinite(minX)) return null;
    return { minX, minY, maxX, maxY };
};

// Центрирование текста на стрелке
export const centerTextOnArrow = (arrow) => {
    let mx, my;
    if (arrow.pathPoints && arrow.pathPoints.length >= 2) {
        if (arrow.pathPoints.length >= 4) {
            mx = (arrow.pathPoints[1].x + arrow.pathPoints[2].x) / 2;
            my = (arrow.pathPoints[1].y + arrow.pathPoints[2].y) / 2;
        } else {
            const pts = arrow.pathPoints;
            mx = (pts[0].x + pts[pts.length - 1].x) / 2;
            my = (pts[0].y + pts[pts.length - 1].y) / 2;
        }
    } else {
        mx = (arrow.x + arrow.x2) / 2;
        my = (arrow.y + arrow.y2) / 2;
    }

    // For C4 horizontal arrows raise the label above the arrow line
    if (arrow.c4) {
        const pts = arrow.pathPoints && arrow.pathPoints.length >= 2
            ? arrow.pathPoints
            : [{ x: arrow.x, y: arrow.y }, { x: arrow.x2 ?? arrow.x, y: arrow.y2 ?? arrow.y }];
        const first = pts[0], last = pts[pts.length - 1];
        if (Math.abs(last.x - first.x) >= Math.abs(last.y - first.y)) {
            return { x: mx, y: my - 20 };
        }
    }

    return { x: mx, y: my };
};

// Получение координат мыши в мировых координатах
export const getMouseWorld = (e, canvasRef, zoom) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
        x: (e.clientX - r.left) / zoom,
        y: (e.clientY - r.top) / zoom
    };
};

// Проверка является ли элемент редактируемым
export const isEditableElement = (target) => {
    if (!target) return false;
    let el = target;
    while (el) {
        const tag = (el.tagName || '').toLowerCase();
        if (
            tag === 'input' ||
            tag === 'textarea' ||
            el.isContentEditable ||
            (el.classList &&
                Array.from(el.classList).some((c) =>
                    c.includes('monaco-editor')
                ))
        )
            return true;
        el = el.parentElement;
    }
    return false;
};
